<script setup lang="ts">
    import { useToast } from 'primevue/usetoast';

    const authStore = useAuthStore();
    const branchesStore = useBranchesStore();
    const assetsStore = useAssetsStore();
    const toast = useToast();

    const props = defineProps({
        'type': String,
        'data': { type: Object, default: {} },
        showOtherType: { type: Boolean, default: true }
    });
    const emits = defineEmits(['close']);

    const customer = ref({ ...props.data, debt_type: 'negative' });
    const trader = ref({
        ...props.data,
        debt_manufacturing_type: 'negative',
        debt_gold: Object.assign(...assetsStore.golds.filter(g => g.gold_type === 'old').map(g => ({
            [g.code]: {
                type: 'negative',
                debt: null
            }
        })))
    });

    const countries = ref([]);
    const defaultCountry = ref({});
    const debtLoading = ref(false);

    onBeforeMount(() => {
        authStore.request().get('api/countries').then(({ data }) => {
            countries.value = data;

            if (props.data?.phone) {
                const phonePrefix = props.data.phone.split(' ')[0];

                defaultCountry.value = data.find((country: object) => country.phone_prefix === phonePrefix);
            } else {
                defaultCountry.value = data.find((country: object) => country.iso2 === (branchesStore.getBranch(authStore.branch_id).country_iso || authStore.company.country_iso));
            }
        });

        if (props.data?.id) {
            debtLoading.value = true;

            if (props.type === 'customers') {
                authStore.request().get('api/customers/' + props.data.id).then(({ data }) => {
                    if (data.initial_balance_transaction) {
                        customer.value.debt = Math.abs(data.initial_balance_transaction.amount);
                        customer.value.debt_type = Number(data.initial_balance_transaction.amount) > 0 ? 'positive' : 'negative';
                    }

                    debtLoading.value = false;
                });
            } else {
                authStore.request().get('api/traders/' + props.data.id).then(({ data }) => {
                    if (data.initial_manufacturing_balance_transaction) {
                        trader.value.debt_manufacturing = Math.abs(data.initial_manufacturing_balance_transaction.amount);
                        trader.value.debt_manufacturing_type = Number(data.initial_manufacturing_balance_transaction.amount) > 0 ? 'positive' : 'negative';
                    }

                    if (data.initial_gold_balance_transactions?.length) {
                        for (const gt of data.initial_gold_balance_transactions) {
                            trader.value.debt_gold[gt.asset_code] = {
                                type: Number(gt.amount) > 0 ? 'positive' : 'negative',
                                value: Math.abs(gt.amount)
                            };
                        }
                    }

                    debtLoading.value = false;
                });
            }
        }
    });

    const errors = ref([]);
    const traderErrors = ref([]);
    const loadingCustomer = ref(false);
    const loadingTrader = ref(false);

    function saveCustomer() {
        const validate = [];
        if (!customer.value.name) {
            errors.value.name = 'اسم الزبون مطلوب.';
            validate.push(false);
        } else {
            errors.value.name = null;
        }

        if (validate.filter(v => !v).length) {
            toast.add({
                severity: 'error',
                summary: 'بيانات خاطئة',
                detail: 'الرجاء التأكد من البيانات المدخلة.',
                life: 10000
            });

            return;
        } else {
            loadingCustomer.value = true;

            let data = { ...customer.value };

            data.debt = data.debt ? (data.debt_type === 'negative' ? (data.debt * -1) : data.debt) : null;
            data.identifier = data.identifier ? String(data.identifier) : null;

            authStore.request()
                .request({
                    method: props.data?.id ? 'PUT' : 'POST',
                    url: props.data?.id ? `/api/customers/${props.data.id}` : '/api/customers',
                    data: data
                })
                .then((res) => {
                    toast.add({
                        severity: 'success',
                        summary: 'تم الحفظ بنجاح',
                        detail: 'تم حفظ الزبون بنجاح.',
                        life: 10000
                    });

                    loadingCustomer.value = false;

                    emits('close', 'customers', res.data);
                })
                .catch((err) => {
                    loadingCustomer.value = false;

                    if (err.status === 422) {
                        Object.entries(err.response.data.errors).forEach(([key, errors]) => {
                            errors.forEach((error) => {
                                toast.add({
                                    severity: 'error',
                                    detail: error,
                                    life: 10000
                                });
                            });
                        });
                    } else {
                        toast.add({
                            severity: 'error',
                            detail: err.response.data.message,
                            life: 10000
                        });
                    }
                });
        }
    }

    function saveTrader() {
        const validate = [];
        if (!trader.value.name) {
            traderErrors.value.name = 'اسم التاجر مطلوب.';
            validate.push(false);
        } else {
            traderErrors.value.name = null;
        }
        if (!trader.value.vat_number) {
            traderErrors.value.vat_number = 'الرقم الضريبي مطلوب.';
            validate.push(false);
        } else {
            traderErrors.value.vat_number = null;
        }
        if (!trader.value.company) {
            traderErrors.value.company = 'اسم الشركة مطلوب.';
            validate.push(false);
        } else {
            traderErrors.value.company = null;
        }

        if (validate.filter(v => !v).length) {
            toast.add({
                severity: 'error',
                summary: 'بيانات خاطئة',
                detail: 'الرجاء التأكد من البيانات المدخلة.',
                life: 10000
            });

            return;
        } else {
            loadingTrader.value = true;

            let data = { ...trader.value };

            data.identifier = data.identifier ? String(data.identifier) : null;
            data.debt_manufacturing = data.debt_manufacturing ? (data.debt_manufacturing_type === 'negative' ? (data.debt_manufacturing * -1) : data.debt_manufacturing) : null;
            data.debt_gold = Object.keys(data.debt_gold).map(goldCode => {
                const debt = data.debt_gold[goldCode];

                return {
                    asset_code: goldCode,
                    value: debt.value ? (debt.type === 'negative' ? (debt.value * -1) : debt.value) : null
                };
            });

            authStore.request()
                .request({
                    method: props.data?.id ? 'PUT' : 'POST',
                    url: props.data?.id ? `/api/traders/${props.data.id}` : '/api/traders',
                    data: data
                })
                .then((res) => {
                    toast.add({
                        severity: 'success',
                        summary: 'تم الحفظ بنجاح',
                        detail: 'تم حفظ التاجر بنجاح.',
                        life: 10000
                    });

                    loadingTrader.value = false;

                    emits('close', 'traders', res.data);
                })
                .catch((err) => {
                    loadingTrader.value = false;

                    if (err.status === 422) {
                        Object.entries(err.response.data.errors).forEach(([key, errors]) => {
                            errors.forEach((error) => {
                                toast.add({
                                    severity: 'error',
                                    detail: error,
                                    life: 10000
                                });
                            });
                        });
                    } else {
                        toast.add({
                            severity: 'error',
                            detail: err.response.data.message,
                            life: 10000
                        });
                    }
                });
        }
    }

    import { onBeforeMount, ref, defineEmits, defineProps } from 'vue';
    import { useAuthStore } from '@/stores/auth';
    import { useAssetsStore } from '@/stores/assets';
    import IconX from '@/components/icon/icon-x.vue';
    import { DialogPanel, Tab, TabGroup, TabList, TabPanel, TabPanels } from '@headlessui/vue';
    import merchentIcon from '@/components/icon/gold-sidebar/merchent.vue';
    import customerIcon from '@/components/icon/gold-sidebar/customer.vue';
    import InputPhone from '@/views/components/InputPhone.vue';
    import { useBranchesStore } from '@/stores/branches';
    import { data } from 'autoprefixer';
    import MyInputNumber from '@/views/components/MyInputNumber.vue';
</script>

<template>
    <DialogPanel
        class="panel border-0 p-0 rounded-lg overflow-hidden w-full max-w-xl text-black dark:text-white-dark">
        <button type="button"
                class="absolute top-4 ltr:right-4 rtl:left-4 text-gray-400 hover:text-gray-800 dark:hover:text-gray-600 outline-none"
                @click="() => emits('close')">
            <icon-x />
        </button>
        <div class=" font-tajawal font-semibold text-lg bg-[#fbfbfb] dark:bg-[#121c2c] pr-5 py-3 pl-[50px]">
            {{
                props.data?.id ?
                    (props.type === 'customers' ? 'تعديل زبون' : 'تعديل تاجر')
                    : (props.type === 'customers' ? 'إضافة زبون جديد' : 'إضافة تاجر جديد')
            }}
        </div>

        <!-- tap add new customers / merchent -->
        <TabGroup as="div" class="mb-5" :default-index="props.type === 'customers' ? 0 : 1">
            <TabList class="flex justify-center items-center mt-6">
                <div class="flex space-x-0" v-if="!props.data?.id && props.showOtherType">
                    <Tab as="template" v-slot="{ selected }">
                        <a href="javascript:;"
                           class="flex items-center font-semibold justify-center w-40 px-6 py-2 text-blue-500 border border-blue-500 rounded-r-lg hover:bg-blue-500 hover:text-white transition"
                           :class="{ 'bg-primary text-white': selected }">
                            <customerIcon class="ml-2" />
                            زبون جديد
                        </a>
                    </Tab>
                    <Tab as="template" v-slot="{ selected }">
                        <a href="javascript:;"
                           class="flex items-center font-semibold justify-center w-40 px-6 py-2 text-green-500 border border-green-500 rounded-l-lg hover:bg-green-500 hover:text-white transition"
                           :class="{ 'bg-success text-white': selected }">
                            <merchentIcon class="ml-2" />
                            تاجر جديد
                        </a>
                    </Tab>
                </div>
            </TabList>

            <TabPanels class=" flex-1 text-sm">
                <TabPanel>
                    <div class="p-6">
                        <form class="space-y-3 ">
                            <div>
                                <label for="">اسم الزبون كاملاً</label>
                                <InputText v-model="customer.name" placeholder="أدخل اسم الزبون كاملا"
                                           :invalid="!!errors.name" size="small"
                                           fluid />
                                <Message v-if="errors.name" severity="error" size="small" variant="simple">
                                    {{ errors.name }}
                                </Message>
                            </div>

                            <div>
                                <label for="">رقم الهوية</label>
                                <InputNumber v-model="customer.identifier" placeholder="أدخل رقم الهوية من 9 أرقام"
                                             size="small" inputId="withoutgrouping" :useGrouping="false" fluid />
                            </div>
                            <div>
                                <label class="form-label">رقم الجوال</label>
                                <InputPhone v-model="customer.phone" :countries="countries"
                                            :default-country="defaultCountry" size="small" />
                            </div>
                            <div>
                                <label class="form-label">العنوان</label>
                                <InputText v-model="customer.address" placeholder="أدخل العنوان" size="small" fluid />
                            </div>
                            <div>
                                <label class="form-label">ديون سابقة</label>
                                <div
                                    class="grid grid-cols-12 gap-4 items-center mt-4">
                                    <div class="col-span-3">
                                        <Select v-model="customer.debt_type" :options="[
                                            {id: 'negative', label: 'لنا'},
                                            {id: 'positive', label: 'علينا'},
                                        ]" option-value="id" option-label="label" size="small" fluid />
                                    </div>

                                    <div class="col-span-9">
                                        <Skeleton v-if="debtLoading" height="2rem" />
                                        <MyInputNumber
                                            v-else
                                            v-model="customer.debt"
                                            :digits="assetsStore.default_asset?.decimal_places || 2"
                                            placeholder="0.00"
                                            :suffix="assetsStore.default_asset?.currency?.symbol_ar"
                                        />
                                    </div>
                                </div>
                            </div>

                            <div class="flex justify-center items-center !mt-8">
                                <Button class="!px-6" size="small"
                                        :label="loadingCustomer ? null : (props.data?.id ? 'تعديل' : 'إضافة')"
                                        @click="saveCustomer" :loading="loadingCustomer" />

                                <Button @click="() => emits('close')" :disabled="loadingCustomer" size="small"
                                        label="إلغاء الأمر" severity="danger"
                                        variant="outlined" class="mr-4" />
                            </div>
                        </form>
                    </div>
                </TabPanel>
                <TabPanel>
                    <div class="p-6">
                        <form class="space-y-3 ">
                            <div>
                                <label for="">اسم التاجر</label>
                                <InputText v-model="trader.name" placeholder="أدخل اسم التاجر كاملا"
                                           :invalid="!!traderErrors.name" size="small" fluid />
                                <Message v-if="traderErrors.name" severity="error" size="small" variant="simple">
                                    {{ traderErrors.name }}
                                </Message>
                            </div>
                            <div>
                                <label for="">اسم الشركة</label>
                                <InputText v-model="trader.company" placeholder="أدخل اسم الشركة" size="small" fluid />
                            </div>
                            <div>
                                <label for="">الرقم الضريبي</label>
                                <InputText v-model="trader.vat_number" placeholder="الرقم الضريبي" size="small" fluid />
                            </div>
                            <div>
                                <label for="">رقم الهوية</label>
                                <InputNumber v-model="trader.identifier" placeholder="أدخل رقم الهوية من 9 أرقام"
                                             size="small" inputId="withoutgrouping" :useGrouping="false" fluid />
                            </div>
                            <div>
                                <label class="form-label">رقم الجوال</label>
                                <InputPhone v-model="trader.phone" :countries="countries"
                                            :default-country="defaultCountry" size="small" />
                            </div>
                            <div>
                                <label class="form-label">العنوان</label>
                                <InputText v-model="trader.address" placeholder="أدخل العنوان" size="small" fluid />
                            </div>
                            <div>
                                <label class="form-label">ديون سابقة</label>
                                <div class="grid grid-cols-12 gap-4 items-center mt-4">
                                    <div class="col-span-2">
                                        ديون مصنعية
                                    </div>
                                    <div class="col-span-3">
                                        <Select v-model="trader.debt_manufacturing_type" :options="[
                                            {id: 'negative', label: 'لنا'},
                                            {id: 'positive', label: 'علينا'},
                                        ]" option-value="id" option-label="label" size="small" fluid />
                                    </div>

                                    <div class="col-span-7">
                                        <Skeleton v-if="debtLoading" height="2rem" />
                                        <MyInputNumber
                                            v-else
                                            v-model="trader.debt_manufacturing"
                                            :digits="assetsStore.default_asset?.decimal_places || 2"
                                            placeholder="ديون مصنعية"
                                            :suffix="assetsStore.default_asset?.currency?.symbol_ar"
                                        />
                                    </div>
                                </div>

                                <div v-for="gold in assetsStore.golds.filter(a => a.gold_type === 'old')"
                                     class="grid grid-cols-12 gap-4 items-center mt-4">
                                    <div class="col-span-2">
                                        ديون عيار {{ gold.gold_karat }}
                                    </div>
                                    <div class="col-span-3">
                                        <Select v-model="trader.debt_gold[gold.code].type" :options="[
                                            {id: 'negative', label: 'لنا'},
                                            {id: 'positive', label: 'علينا'},
                                        ]" option-value="id" option-label="label" size="small" fluid />
                                    </div>

                                    <div class="col-span-7">
                                        <Skeleton v-if="debtLoading" height="2rem" />
                                        <MyInputNumber
                                            v-else
                                            v-model="trader.debt_gold[gold.code].value"
                                            :digits="gold.decimal_places || 2"
                                            :placeholder="'ديون ذهب عيار '+ gold.gold_karat"
                                            suffix="جرام"
                                        />
                                    </div>
                                </div>
                            </div>

                            <div class="flex justify-center items-center !mt-8">
                                <Button class="!px-6" size="small"
                                        :label="loadingTrader ? null : (props.data?.id ? 'تعديل' : 'إضافة')"
                                        @click="saveTrader" :loading="loadingTrader" />

                                <Button @click="() => emits('close')" :disabled="loadingTrader" size="small"
                                        label="إلغاء الأمر" severity="danger"
                                        variant="outlined" class="mr-4" />
                            </div>
                        </form>
                    </div>
                </TabPanel>
            </TabPanels>
        </TabGroup>
    </DialogPanel>
</template>

<style scoped>

</style>
